/**
 * Papa John (group "shop", see ./shop.ts): the travelling trader who is in town for one hour a day
 * (PAPA_HOURS[day of month]), sells a daily assortment at 40 % off and answers one question a day.
 *
 * Original flow (new_interf): the main screen shows init_papa_john_active while FWP.PAPA_HERE (render 1506), set by
 * FWP.modify_papa at every time refresh (FWP.ts:4824-4856: `PAPA_HERE = hour == PAPA_HOURS[date]`; a new local day
 * resets last_papa and papa_asked). init('papa_john') opens menu_papa (new_interf 825-831) with 'papa_ask' (visible
 * while papa_asked == 0, render 3800) and 'papa_shop'; papa_shop sets last_papa = now once per day and opens
 * menu_papa_shop, whose assortment render builds from FWP.get_papa_weapons (FWP.ts:6448-6462, day of month % 8).
 */
import { argsObject, knownKey, noArgs, stringArg } from '../args';
import type { RuleContext } from '../context';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import type { PlayerState } from '../state';
import {
  addWeaponCopy,
  CATEGORY_TABLES,
  checkBuyButtons,
  countBuy,
  CURRENCIES,
  idArg,
  newCopy,
  pay,
  perkLevel,
  sameLocalDay,
  weaponOptimisation,
  type Currency,
} from './shop-common';

export type PapaCategory = 'weapons' | 'objs';
export const PAPA_CATEGORIES: readonly PapaCategory[] = ['weapons', 'objs'];

/** Papa's price factor: weapons at 60 % of the (hidden-adjusted) shop price, objects at 100 % (new_interf 5973-5982). */
export const PAPA_PRICE_FACTOR: Readonly<Record<PapaCategory, number>> = { weapons: 0.6, objs: 1 };

/** Papa is in town now: `getHours() == PAPA_HOURS[getDate()]` of FWP.modify_papa (FWP.ts:4854-4855). */
export function papaHere(ctx: Pick<RuleContext, 'now' | 'calendar' | 'defs'>): boolean {
  const c = ctx.calendar(ctx.now);
  return c.hours == ctx.defs.PAPA_HOURS[c.date];
}

/**
 * FWP.get_papa_weapons (FWP.ts:6448-6462): today's assortment `[n, id 1, ..., id n]` of PAPA_WEAPONS / PAPA_OBJECTS
 * indexed by the day of the month % 8.
 */
export function papaAssortment(ctx: Pick<RuleContext, 'now' | 'calendar' | 'defs'>, category: PapaCategory): any[] {
  const date = ctx.calendar(ctx.now).date;
  return category == 'weapons' ? ctx.defs.PAPA_WEAPONS[date % 8] : ctx.defs.PAPA_OBJECTS[date % 8];
}

/**
 * The new-day part of FWP.modify_papa (FWP.ts:4844-4852) as the client had always applied it when Papa could be
 * here: last_papa from an earlier local day means "not visited / not asked today". Returns the values the handlers
 * see; `apply` performs the reset on the state (only after all preconditions passed).
 */
function papaDay(p: PlayerState, ctx: Pick<RuleContext, 'now' | 'calendar'>): { stale: boolean; lastPapa: number; papaAsked: number; apply(): void } {
  const stale = p.last_papa > 0 && !sameLocalDay(ctx, p.last_papa, ctx.now);
  return {
    stale,
    lastPapa: stale ? 0 : p.last_papa,
    papaAsked: stale ? 0 : p.papa_asked,
    apply() {
      if (stale) {
        p.last_papa = 0;
        p.papa_asked = 0;
      }
    },
  };
}

/** new_interf.return_papa_ask_prob (new_interf.ts:7486-7503): round(education * 4 + luck + luck2), raw perk levels. */
export function papaAskProb(p: PlayerState): number {
  const luck = perkLevel(p, 'luck');
  const luck2 = perkLevel(p, 'luck2');
  const education = perkLevel(p, 'education');
  return Math.round(education * 4 + luck + luck2);
}

export interface PapaEnterResult {
  /** The new last_papa (= server time). */
  lastPapa: number;
  /** Today's assortment (DEF keys) of both tabs. */
  weapons: string[];
  objs: string[];
}

export interface PapaBuyArgs {
  category: PapaCategory;
  id: string;
  currency: Currency;
}

export interface PapaBuyResult {
  category: PapaCategory;
  id: string;
  currency: Currency;
  price: number;
  count: number;
  newWeapon: boolean;
}

export interface PapaAskResult {
  /** papa_state 'time_win' (true) or 'time_fail'. */
  win: boolean;
  /** The chance shown on the button (percent). */
  prob: number;
  /** 'time_win' tells tomorrow's hour: PAPA_HOURS[date of now + 1 day] (render 3787-3792); null on 'time_fail'. */
  tomorrowHour: number | null;
}

const list = (a: any[]): string[] => a.slice(1, Number(a[0]) + 1).map(String);

export const papaOps = {
  /**
   * papa.enter: the 'papa_shop' button of menu_papa.
   * Preconditions: Papa is here now (the screen is menu_papa only while PAPA_HERE) and the shop was not opened today
   * (`last_papa != 0` -> papa_state 'shop_fail', no save).
   * Mutations: modify_papa's new-day reset when last_papa is from an earlier day, then last_papa = now.
   */
  'papa.enter': defineOp<Record<string, never>, PapaEnterResult>({
    parseArgs: noArgs,
    run(state, _args, ctx) {
      const p = state.player;
      if (!papaHere(ctx)) throw new RuleError('papa_not_here', {});
      const day = papaDay(p, ctx);
      if (day.lastPapa != 0) throw new RuleError('papa_shop_visited', { lastPapa: day.lastPapa });
      day.apply();
      p.last_papa = ctx.now;
      return { state, result: { lastPapa: ctx.now, weapons: list(papaAssortment(ctx, 'weapons')), objs: list(papaAssortment(ctx, 'objs')) } };
    },
  }),

  /**
   * papa.buy { category: weapons|objs, id, currency }: 'buy_gold' / 'buy_diam' on menu_papa_shop (new_interf
   * 5972-6020).
   * Preconditions: today's Papa shop session is open (last_papa is today: papa.enter ran today; the original allowed
   * any number of purchases while the screen stayed open), the item is in today's assortment (render 3819-3831), and
   * the buy button is visible (render 3885-3911: price x0.6 for weapons, no Merchant discount, level/req only for
   * gold, limit).
   * Mutations: the item (+ weapon_optimisation), the price, and achiev.buys++ for EVERY purchase - also objects and
   * class-1 weapons (a quirk of the original that is kept). No tasks.
   */
  'papa.buy': defineOp<PapaBuyArgs, PapaBuyResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['category', 'id', 'currency']);
      return { category: stringArg(o, 'category', { oneOf: PAPA_CATEGORIES }), id: idArg(o), currency: stringArg(o, 'currency', { oneOf: CURRENCIES }) };
    },
    run(state, { category, id, currency }, ctx) {
      const p = state.player;
      const { def: defName, store: storeName } = CATEGORY_TABLES[category];
      const table: Record<string, any> = ctx.defs[defName];
      knownKey(table, id);
      if (!(p.last_papa > 0 && sameLocalDay(ctx, p.last_papa, ctx.now))) throw new RuleError('papa_shop_closed', { lastPapa: p.last_papa });
      if (!list(papaAssortment(ctx, category)).includes(id)) throw new RuleError('not_available', { id, reason: 'not_in_assortment' });
      const def = table[id];
      const { price } = checkBuyButtons(p, ctx.defs, category, id, def, currency, PAPA_PRICE_FACTOR[category]);

      const store: Record<string, any> = p[storeName];
      if (category == 'weapons') {
        addWeaponCopy(store, id, newCopy());
        weaponOptimisation(store);
      } else if (!store[id]) {
        store[id] = 1;
      } else {
        ++store[id];
      }
      pay(p, currency, price);
      countBuy(p);
      const newWeapon = category == 'weapons' && store[id][0] == 1;
      const count = category == 'weapons' ? store[id][0] : store[id];
      return { state, result: { category, id, currency, price, count, newWeapon } };
    },
  }),

  /**
   * papa.ask: 'papa_ask' of menu_papa (new_interf 5324-5334).
   * Preconditions: Papa is here now, and the question was not asked today (the button is visible only while
   * papa_asked == 0, render 3800).
   * Mutations: modify_papa's new-day reset when due, papa_asked = 1; one draw true_random(0, 100) against
   * return_papa_ask_prob decides the answer ('time_win' names tomorrow's hour, render 3787-3792).
   */
  'papa.ask': defineOp<Record<string, never>, PapaAskResult>({
    parseArgs: noArgs,
    run(state, _args, ctx) {
      const p = state.player;
      if (!papaHere(ctx)) throw new RuleError('papa_not_here', {});
      const day = papaDay(p, ctx);
      // eslint-disable-next-line eqeqeq
      if (!((day.papaAsked as any) == 0)) throw new RuleError('already_asked', {});
      day.apply();
      p.papa_asked = 1;
      const prob = papaAskProb(p);
      const win = prob > ctx.rng.int(0, 100);
      const tomorrowHour = win ? ctx.defs.PAPA_HOURS[ctx.calendar(ctx.now + 86400).date] : null;
      return { state, result: { win, prob, tomorrowHour } };
    },
  }),
};
